import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import { getCongresses, createCongress, updateCongress, deleteCongress } from '../api';

function Congresses({ user, onLogout }) {
  const [congresses, setCongresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    start_date: '',
    end_date: '',
    description: '',
    website: '',
    participation_type: '',
    presentation_title: '',
    notes: ''
  });
  const [error, setError] = useState('');

  useEffect(() => {
    loadCongresses();
  }, []);

  const loadCongresses = async () => {
    try {
      const response = await getCongresses();
      setCongresses(response.data);
    } catch (err) {
      setError('Error loading congresses');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      location: '',
      start_date: '',
      end_date: '',
      description: '',
      website: '',
      participation_type: '',
      presentation_title: '',
      notes: ''
    });
    setEditingId(null);
    setError('');
  };

  const handleAdd = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (congress) => {
    setFormData({
      name: congress.name || '',
      location: congress.location || '',
      start_date: congress.start_date || '',
      end_date: congress.end_date || '',
      description: congress.description || '',
      website: congress.website || '',
      participation_type: congress.participation_type || '',
      presentation_title: congress.presentation_title || '',
      notes: congress.notes || ''
    });
    setEditingId(congress._id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this congress?')) {
      try {
        setError('');
        console.log('Deleting congress with ID:', id);
        const response = await deleteCongress(id);
        console.log('Delete response:', response);
        await loadCongresses();
        alert('Congress deleted successfully');
      } catch (err) {
        const errorMsg = err.response?.data?.error || 'Error deleting congress';
        setError(errorMsg);
        console.error('Delete congress error:', err);
        console.error('Error response:', err.response?.data);
        alert(`Failed to delete congress: ${errorMsg}`);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingId) {
        await updateCongress(editingId, formData);
      } else {
        await createCongress(formData);
      }
      setShowModal(false);
      resetForm();
      loadCongresses();
    } catch (err) {
      setError(err.response?.data?.error || 'Error saving congress');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="dashboard-container">
      <Sidebar user={user} onLogout={onLogout} />
      
      <div className="main-content">
        <div className="page-header">
          <h1>Congresses</h1>
          <p>Track your conference and congress participation</p>
        </div>

        <div className="content-section">
          <div className="section-header">
            <h2>My Congresses</h2>
            <button className="btn btn-success" onClick={handleAdd}>
              + Add Congress
            </button>
          </div>

          {loading ? (
            <div>Loading...</div>
          ) : congresses.length === 0 ? (
            <div className="empty-state">
              <p>No congresses yet</p>
              <button className="btn btn-primary" onClick={handleAdd}>
                Add your first congress
              </button>
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Location</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th>Participation</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {congresses.map((congress) => (
                    <tr key={congress._id}>
                      <td data-label="Name">{congress.name}</td>
                      <td data-label="Location">{congress.location || '-'}</td>
                      <td data-label="Start Date">{congress.start_date || '-'}</td>
                      <td data-label="End Date">{congress.end_date || '-'}</td>
                      <td data-label="Participation">{congress.participation_type || '-'}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn btn-primary btn-small"
                            onClick={() => handleEdit(congress)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn btn-danger btn-small"
                            onClick={() => handleDelete(congress._id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingId ? 'Edit Congress' : 'Add Congress'}</h2>
                <button className="close-btn" onClick={() => setShowModal(false)}>×</button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {error && <div className="error-message">{error}</div>}
                  
                  <div className="form-group">
                    <label>Congress Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Location</label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Start Date</label>
                    <input
                      type="date"
                      name="start_date"
                      value={formData.start_date}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>End Date</label>
                    <input
                      type="date"
                      name="end_date"
                      value={formData.end_date}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Participation Type</label>
                    <select
                      name="participation_type"
                      value={formData.participation_type}
                      onChange={handleChange}
                    >
                      <option value="">Select...</option>
                      <option value="Presenter">Presenter</option>
                      <option value="Attendee">Attendee</option>
                      <option value="Organizer">Organizer</option>
                      <option value="Speaker">Speaker</option>
                      <option value="Poster">Poster Presentation</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label>Presentation Title</label>
                    <input
                      type="text"
                      name="presentation_title"
                      value={formData.presentation_title}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Website</label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      placeholder="https://..."
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                    />
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-success">
                    {editingId ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Congresses;
